import { formatDistanceStrict, parseISO } from 'date-fns';

import type { AppConfigDtOverride } from '#/config/types/AppConfig';
import { I18nLocale } from '#/i18n/types/I18nLocale';

import {
  getCalendarDayStartForOffset,
  getOffsetMinutes,
  normalizeDateInput,
} from './domain';
import { getDateFnsLocale } from './locale';

export function resolveCurrentDateTime(dtOverride: AppConfigDtOverride) {
  if (dtOverride) {
    return parseISO(dtOverride);
  }

  return new Date();
}

export function now({ dtOverride }: { dtOverride: AppConfigDtOverride }) {
  return resolveCurrentDateTime(dtOverride);
}

function distanceInWordsToNow({
  date,
  locale,
  dtOverride,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  dtOverride: AppConfigDtOverride;
}) {
  return formatDistanceStrict(
    normalizeDateInput(date),
    resolveCurrentDateTime(dtOverride),
    {
      addSuffix: true,
      locale: getDateFnsLocale(locale),
      roundingMethod: 'floor',
    },
  );
}

export function formatRelativeToNow(
  args: Parameters<typeof distanceInWordsToNow>[0],
) {
  return distanceInWordsToNow(args);
}

function getComparisonDayStart({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return getCalendarDayStartForOffset(
    resolveCurrentDateTime(dtOverride),
    getOffsetMinutes(date),
  );
}

function getTargetDayStart(date: string | Date) {
  return getCalendarDayStartForOffset(
    new Date(normalizeDateInput(date)),
    getOffsetMinutes(date),
  );
}

function dateIsToday({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return (
    getComparisonDayStart({ date, dtOverride }) === getTargetDayStart(date)
  );
}

export function isTodayInOffset(args: Parameters<typeof dateIsToday>[0]) {
  return dateIsToday(args);
}

function dateIsUpcoming({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return getTargetDayStart(date) >= getComparisonDayStart({ date, dtOverride });
}

export function isUpcomingInOffset(args: Parameters<typeof dateIsUpcoming>[0]) {
  return dateIsUpcoming(args);
}

function dateIsPast({
  date,
  dtOverride,
}: {
  date: string | Date;
  dtOverride: AppConfigDtOverride;
}) {
  return !dateIsUpcoming({ date, dtOverride });
}

export function isPastInOffset(args: Parameters<typeof dateIsPast>[0]) {
  return dateIsPast(args);
}

export function getCurrentYear({
  dtOverride,
}: {
  dtOverride: AppConfigDtOverride;
}) {
  return resolveCurrentDateTime(dtOverride).getFullYear();
}
